import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { DragControls } from 'three/examples/jsm/controls/DragControls';
import { NURBSSurface } from 'three/examples/jsm/curves/NURBSSurface.js';
import { ParametricGeometry } from 'three/examples/jsm/geometries/ParametricGeometry.js';
import { SUBTRACTION, INTERSECTION, ADDITION, Brush, Evaluator } from 'three-bvh-csg';

interface SplineEditorProps {
  width?: number;
  height?: number;
}

interface SplineCurve {
  points: THREE.Mesh[];
  curve: THREE.CatmullRomCurve3;
  line: THREE.Line;
  zPosition: number;
}

const SplineEditor: React.FC<SplineEditorProps> = ({ width = 800, height = 600 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const rendererRef = useRef<THREE.WebGLRenderer>();
  const controlsRef = useRef<OrbitControls>();
  const dragControlsRef = useRef<DragControls>();
  const splineCurvesRef = useRef<SplineCurve[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState<THREE.Mesh | null>(null);
  const hoveredPointRef = useRef<THREE.Mesh | null>(null);
  const defaultColorRef = useRef<number>(0xff0000);
  const hoverColorRef = useRef<number>(0x00ff00);
  const selectedColorRef = useRef<number>(0x0000ff);
  const initialPositionRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const [pointCount, setPointCount] = useState(7);
  const [isAddingPoint, setIsAddingPoint] = useState(false);
  const raycasterRef = useRef<THREE.Raycaster>(new THREE.Raycaster());
  const mouseRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const planeRef = useRef<THREE.Plane>(new THREE.Plane(new THREE.Vector3(0, 0, 1), 0));
  const planeIntersectRef = useRef<THREE.Vector3>(new THREE.Vector3());
  const isInitializedRef = useRef<boolean>(false);
  const [activeCurveIndex, setActiveCurveIndex] = useState(0);
  const nurbsSurfaceRef = useRef<THREE.Mesh | null>(null);
  const [showNurbsSurface, setShowNurbsSurface] = useState(false);
  const cylinderRef = useRef<THREE.Mesh | null>(null);
  const [showCylinder, setShowCylinder] = useState(false);
  const sphereRef = useRef<THREE.Mesh | null>(null);
  const [showSphere, setShowSphere] = useState(false);
  const intersectionRef = useRef<THREE.Points | null>(null);
  const [showIntersection, setShowIntersection] = useState(false);
  const sphereCylinderIntersectionRef = useRef<THREE.Points | null>(null);
  const [showSphereCylinderIntersection, setShowSphereCylinderIntersection] = useState(false);
  const evaluatorRef = useRef<Evaluator | null>(null);

  // Function to create a new control point
  const createControlPoint = (position: THREE.Vector3, scene: THREE.Scene) => {
    const sphereGeometry = new THREE.SphereGeometry(0.2, 32, 32);
    const sphereMaterial = new THREE.MeshPhongMaterial({
      color: defaultColorRef.current,
      shininess: 100,
      specular: new THREE.Color(0xffffff),
      transparent: true,
      opacity: 0.9
    });
    
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    sphere.position.copy(position);
    sphere.castShadow = true;
    sphere.receiveShadow = true;
    scene.add(sphere);
    
    return sphere;
  };

  // Function to add a new control point at a specific position
  const addControlPointAtPosition = (position: THREE.Vector3, curveIndex: number) => {
    if (!sceneRef.current) return;
    
    // Create the new point
    const newPoint = createControlPoint(position, sceneRef.current);
    
    // Add to the active curve
    splineCurvesRef.current[curveIndex].points.push(newPoint);
    
    // Update the spline
    updateSpline(curveIndex);
    
    // Update drag controls
    if (dragControlsRef.current) {
      dragControlsRef.current.getObjects().push(newPoint);
    }
    
    // Update point count
    setPointCount(splineCurvesRef.current[curveIndex].points.length);
    
    // Update NURBS surface if it's visible
    if (showNurbsSurface) {
      updateNurbsSurface();
    }
    
    return newPoint;
  };

  // Function to add a new control point at the end
  const addControlPoint = () => {
    if (!sceneRef.current || !splineCurvesRef.current[activeCurveIndex].points.length) return;
    
    // Get the last point's position
    const lastPoint = splineCurvesRef.current[activeCurveIndex].points[splineCurvesRef.current[activeCurveIndex].points.length - 1];
    const newPosition = lastPoint.position.clone().add(new THREE.Vector3(1, 0, 0));
    
    // Create the new point
    addControlPointAtPosition(newPosition, activeCurveIndex);
  };

  // Function to remove the selected control point
  const removeSelectedPoint = () => {
    if (!sceneRef.current || !selectedPoint || splineCurvesRef.current[activeCurveIndex].points.length <= 2) return;
    
    // Remove from scene
    sceneRef.current.remove(selectedPoint);
    
    // Remove from array
    const index = splineCurvesRef.current[activeCurveIndex].points.indexOf(selectedPoint);
    if (index !== -1) {
      splineCurvesRef.current[activeCurveIndex].points.splice(index, 1);
    }
    
    // Update drag controls
    if (dragControlsRef.current) {
      const objects = dragControlsRef.current.getObjects();
      const objectIndex = objects.indexOf(selectedPoint);
      if (objectIndex !== -1) {
        objects.splice(objectIndex, 1);
      }
    }
    
    // Reset selected point
    setSelectedPoint(null);
    
    // Update the spline
    updateSpline(activeCurveIndex);
    
    // Update point count
    setPointCount(splineCurvesRef.current[activeCurveIndex].points.length);
    
    // Update NURBS surface if it's visible
    if (showNurbsSurface) {
      updateNurbsSurface();
    }
  };

  // Function to update the spline
  const updateSpline = (curveIndex: number) => {
    const curve = splineCurvesRef.current[curveIndex];
    
    if (curve.points.length < 2) {
      // If we have less than 2 points, remove the spline line
      if (curve.line && sceneRef.current) {
        sceneRef.current.remove(curve.line);
        curve.line = undefined as any;
      }
      return;
    }
    
    if (curve.curve && curve.line) {
      const newPoints = curve.points.map(point => point.position.clone());
      curve.curve.points = newPoints;
      
      const curvePoints = curve.curve.getPoints(50);
      curve.line.geometry.setFromPoints(curvePoints);
      curve.line.geometry.attributes.position.needsUpdate = true;
    } else if (curve.points.length >= 2) {
      // Create a new spline curve if we don't have one yet
      const points = curve.points.map(point => point.position.clone());
      const newCurve = new THREE.CatmullRomCurve3(points);
      curve.curve = newCurve;
      
      const curvePoints = newCurve.getPoints(50);
      const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
      const material = new THREE.LineBasicMaterial({ color: 0x000000 });
      const splineObject = new THREE.Line(geometry, material);
      
      if (sceneRef.current) {
        sceneRef.current.add(splineObject);
      }
      curve.line = splineObject;
    }
  };

  // Function to create and update the NURBS surface
  const updateNurbsSurface = () => {
    if (!sceneRef.current || splineCurvesRef.current.length < 2) return;
    
    // Remove existing NURBS surface if it exists
    if (nurbsSurfaceRef.current && sceneRef.current) {
      sceneRef.current.remove(nurbsSurfaceRef.current);
      nurbsSurfaceRef.current = null;
    }
    
    // Create control points for the NURBS surface
    // We need to organize the points in a grid format for NURBS
    const degree1 = 3; // Degree in u direction (along each curve)
    const degree2 = 3; // Degree in v direction (across curves)
    
    // Create a grid of control points from the spline curves
    const controlPoints: THREE.Vector4[][] = [];
    
    // We need exactly 7 points in the u direction (along each curve)
    // and 8 points in the v direction (across all curves)
    const uPoints = 7;
    const vPoints = 8; // Using all 8 curves
    
    // For each point index in the u direction (along each curve)
    for (let i = 0; i < uPoints; i++) {
      const row: THREE.Vector4[] = [];
      
      // For each curve in the v direction (across curves)
      for (let j = 0; j < vPoints; j++) {
        if (j < splineCurvesRef.current.length) {
          const curve = splineCurvesRef.current[j];
          
          // If we have enough points in this curve
          if (i < curve.points.length) {
            const point = curve.points[i].position;
            // Create a Vector4 with weight 1
            row.push(new THREE.Vector4(point.x, point.y, point.z, 1));
          } else {
            // If this curve has fewer points, use the last point
            const lastPoint = curve.points[curve.points.length - 1].position;
            row.push(new THREE.Vector4(lastPoint.x, lastPoint.y, lastPoint.z, 1));
          }
        } else {
          // If we have fewer curves than needed, use the last curve
          const lastCurve = splineCurvesRef.current[splineCurvesRef.current.length - 1];
          if (i < lastCurve.points.length) {
            const point = lastCurve.points[i].position;
            row.push(new THREE.Vector4(point.x, point.y, point.z, 1));
          } else {
            const lastPoint = lastCurve.points[lastCurve.points.length - 1].position;
            row.push(new THREE.Vector4(lastPoint.x, lastPoint.y, lastPoint.z, 1));
          }
        }
      }
      
      controlPoints.push(row);
    }
    
    // Create knots arrays with proper size for 8 curves
    // For degree 3, we need (degree + 1) zeros at the start and end
    // and (number of control points - degree - 1) evenly spaced values in between
    const knots1 = new Array(degree1 + 1).fill(0).concat(
      Array.from({ length: uPoints - degree1 - 1 }, (_, i) => (i + 1) / (uPoints - degree1))
    ).concat(new Array(degree1 + 1).fill(1));
    
    const knots2 = new Array(degree2 + 1).fill(0).concat(
      Array.from({ length: vPoints - degree2 - 1 }, (_, i) => (i + 1) / (vPoints - degree2))
    ).concat(new Array(degree2 + 1).fill(1));
    
    // Create the NURBS surface
    const nurbsSurface = new NURBSSurface(degree1, degree2, knots1, knots2, controlPoints);
    
    // Function to get a point on the surface
    const getSurfacePoint = (u: number, v: number, target: THREE.Vector3) => {
      return nurbsSurface.getPoint(u, v, target);
    };
    
    // Create the geometry with higher resolution
    const geometry = new ParametricGeometry(getSurfacePoint, 50, 50);
    
    // Ensure the geometry is properly closed for CSG operations
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    
    // Create a more visually appealing material
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x156289,
      metalness: 0.2,
      roughness: 0.3,
      clearcoat: 0.5,
      clearcoatRoughness: 0.2,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.85,
      envMapIntensity: 1.0,
      reflectivity: 0.5
    });
    
    // Create the mesh
    const surface = new THREE.Mesh(geometry, material);
    surface.position.set(0, 0, 0);
    
    // Add to scene
    if (sceneRef.current) {
      sceneRef.current.add(surface);
    }
    
    // Store reference
    nurbsSurfaceRef.current = surface;
    
    // Log the control points for debugging
    console.log("NURBS control points:", controlPoints.length, "rows,", controlPoints[0]?.length, "columns");
    console.log("Control points matrix structure:", controlPoints.map(row => 
      row.map(point => `(${point.x.toFixed(2)}, ${point.y.toFixed(2)}, ${point.z.toFixed(2)})`).join(", ")
    ));
  };

  // Function to create and update the cylinder
  const updateCylinder = () => {
    if (!sceneRef.current) return;
    
    // Remove existing cylinder if it exists
    if (cylinderRef.current && sceneRef.current) {
      sceneRef.current.remove(cylinderRef.current);
      cylinderRef.current = null;
    }
    
    // Create cylinder geometry
    const radius = 0.8;
    const height = 10;
    const geometry = new THREE.CylinderGeometry(radius, radius, height, 32);
    
    // Create material
    const material = new THREE.MeshPhysicalMaterial({
      color: 0xff0000,
      metalness: 0.3,
      roughness: 0.4,
      clearcoat: 0.5,
      clearcoatRoughness: 0.2,
      transparent: true,
      opacity: 0.8
    });
    
    // Create mesh
    const cylinder = new THREE.Mesh(geometry, material);
    
    // Position at the center of the scene
    cylinder.position.set(0, 0, 0);
    
    // Add to scene
    sceneRef.current.add(cylinder);
    
    // Store reference
    cylinderRef.current = cylinder;
  };

  // Function to create and update the sphere
  const updateSphere = () => {
    if (!sceneRef.current) return;
    
    // Remove existing sphere if it exists
    if (sphereRef.current && sceneRef.current) {
      sceneRef.current.remove(sphereRef.current);
      sphereRef.current = null;
    }
    
    // Create sphere geometry
    const radius = 1.5; // Increased radius to ensure intersection with cylinder
    const geometry = new THREE.SphereGeometry(radius, 32, 32);
    
    // Create material
    const material = new THREE.MeshPhysicalMaterial({
      color: 0x0000ff,
      metalness: 0.3,
      roughness: 0.4,
      clearcoat: 0.5,
      clearcoatRoughness: 0.2,
      transparent: true,
      opacity: 0.8
    });
    
    // Create mesh
    const sphere = new THREE.Mesh(geometry, material);
    
    // Position at the center of the scene
    sphere.position.set(0, 0, 0);
    
    // Add to scene
    sceneRef.current.add(sphere);
    
    // Store reference
    sphereRef.current = sphere;
    
    console.log("Sphere created with radius:", radius);
  };

  // Function to create a new spline curve
  const createSplineCurve = (zPosition: number, scene: THREE.Scene) => {
    // Create initial spline points (7 points)
    // Move first and last points further away from other points
    const points = [
      new THREE.Vector3(-3, 0, zPosition),  // Moved further left
      new THREE.Vector3(-1, 0, zPosition),
      new THREE.Vector3(-0.5, 0, zPosition),
      new THREE.Vector3(0, 0, zPosition),  // Center point
      new THREE.Vector3(0.5, 0, zPosition),
      new THREE.Vector3(1, 0, zPosition),
      new THREE.Vector3(3, 0, zPosition),  // Moved further right
    ];

    // Create point spheres
    const controlPoints: THREE.Mesh[] = [];
    points.forEach((point) => {
      const sphere = createControlPoint(point, scene);
      controlPoints.push(sphere);
    });

    // Create spline curve
    const curve = new THREE.CatmullRomCurve3(points);
    
    // Create spline line
    const curvePoints = curve.getPoints(50);
    const geometry = new THREE.BufferGeometry().setFromPoints(curvePoints);
    const material = new THREE.LineBasicMaterial({ color: 0x000000 });
    const splineObject = new THREE.Line(geometry, material);
    scene.add(splineObject);
    
    // Create and return the spline curve object
    return {
      points: controlPoints,
      curve: curve,
      line: splineObject,
      zPosition: zPosition
    };
  };

  // Function to update the intersection
  const updateIntersection = () => {
    if (!sceneRef.current || !nurbsSurfaceRef.current || !cylinderRef.current) return;
    
    // Remove existing intersection if it exists
    if (intersectionRef.current) {
      sceneRef.current.remove(intersectionRef.current);
      intersectionRef.current = null;
    }
    
    // Create a simplified visualization of the intersection
    const nurbsGeometry = nurbsSurfaceRef.current.geometry;
    const cylinderGeometry = cylinderRef.current.geometry;
    
    // Create a new geometry for the intersection
    const intersectionGeometry = new THREE.BufferGeometry();
    
    // Get the positions from both geometries
    const nurbsPositions = nurbsGeometry.attributes.position;
    const cylinderPositions = cylinderGeometry.attributes.position;
    
    // Create a new array for the intersection points
    const intersectionPoints: number[] = [];
    
    // Find points that are close to both the NURBS surface and the cylinder
    for (let i = 0; i < nurbsPositions.count; i++) {
      const nurbsPoint = new THREE.Vector3();
      nurbsPoint.fromBufferAttribute(nurbsPositions, i);
      
      // Check if this point is close to the cylinder
      let isCloseToCylinder = false;
      for (let j = 0; j < cylinderPositions.count; j++) {
        const cylinderPoint = new THREE.Vector3();
        cylinderPoint.fromBufferAttribute(cylinderPositions, j);
        
        if (nurbsPoint.distanceTo(cylinderPoint) < 0.1) {
          isCloseToCylinder = true;
          break;
        }
      }
      
      if (isCloseToCylinder) {
        intersectionPoints.push(nurbsPoint.x, nurbsPoint.y, nurbsPoint.z);
      }
    }
    
    // Set the intersection points
    intersectionGeometry.setAttribute('position', new THREE.Float32BufferAttribute(intersectionPoints, 3));
    
    // Create a material for the intersection
    const intersectionMaterial = new THREE.PointsMaterial({
      color: 0x00ff00,
      size: 0.05,
      sizeAttenuation: true
    });
    
    // Create the intersection mesh
    const intersectionMesh = new THREE.Points(intersectionGeometry, intersectionMaterial);
    intersectionRef.current = intersectionMesh;
    
    // Add the intersection to the scene
    sceneRef.current.add(intersectionMesh);
  };

  // Function to update the sphere-cylinder intersection
  const updateSphereCylinderIntersection = () => {
    if (!sceneRef.current || !sphereRef.current || !cylinderRef.current) {
      console.error("Missing required objects for sphere-cylinder intersection:", {
        scene: !!sceneRef.current,
        sphere: !!sphereRef.current,
        cylinder: !!cylinderRef.current
      });
      return;
    }
    
    // Remove existing intersection if it exists
    if (sphereCylinderIntersectionRef.current) {
      sceneRef.current.remove(sphereCylinderIntersectionRef.current);
      sphereCylinderIntersectionRef.current = null;
    }
    
    // Create a simplified visualization of the intersection
    const sphereGeometry = sphereRef.current.geometry;
    const cylinderGeometry = cylinderRef.current.geometry;
    
    // Create a new geometry for the intersection
    const intersectionGeometry = new THREE.BufferGeometry();
    
    // Get the positions from both geometries
    const spherePositions = sphereGeometry.attributes.position;
    const cylinderPositions = cylinderGeometry.attributes.position;
    
    console.log("Sphere positions count:", spherePositions.count);
    console.log("Cylinder positions count:", cylinderPositions.count);
    
    // Create a new array for the intersection points
    const intersectionPoints: number[] = [];
    
    // Find points that are close to both the sphere and the cylinder
    for (let i = 0; i < spherePositions.count; i++) {
      const spherePoint = new THREE.Vector3();
      spherePoint.fromBufferAttribute(spherePositions, i);
      
      // Transform the point to world space
      spherePoint.applyMatrix4(sphereRef.current!.matrixWorld);
      
      // Check if this point is close to the cylinder
      let isCloseToCylinder = false;
      for (let j = 0; j < cylinderPositions.count; j++) {
        const cylinderPoint = new THREE.Vector3();
        cylinderPoint.fromBufferAttribute(cylinderPositions, j);
        
        // Transform the point to world space
        cylinderPoint.applyMatrix4(cylinderRef.current!.matrixWorld);
        
        // Increased threshold for finding intersection points
        if (spherePoint.distanceTo(cylinderPoint) < 0.2) {
          isCloseToCylinder = true;
          break;
        }
      }
      
      if (isCloseToCylinder) {
        intersectionPoints.push(spherePoint.x, spherePoint.y, spherePoint.z);
      }
    }
    
    console.log("Found intersection points:", intersectionPoints.length / 3);
    
    // Set the intersection points
    intersectionGeometry.setAttribute('position', new THREE.Float32BufferAttribute(intersectionPoints, 3));
    
    // Create a material for the intersection
    const intersectionMaterial = new THREE.PointsMaterial({
      color: 0xffff00,
      size: 0.1, // Increased size for better visibility
      sizeAttenuation: true
    });
    
    // Create the intersection mesh
    const intersectionMesh = new THREE.Points(intersectionGeometry, intersectionMaterial);
    sphereCylinderIntersectionRef.current = intersectionMesh;
    
    // Add the intersection to the scene
    sceneRef.current.add(intersectionMesh);
    
    console.log("Sphere-cylinder intersection added to scene");
  };

  // Function to toggle the intersection
  const toggleIntersection = () => {
    if (showIntersection) {
      // Remove the intersection
      if (intersectionRef.current && sceneRef.current) {
        sceneRef.current.remove(intersectionRef.current);
        intersectionRef.current = null;
      }
      setShowIntersection(false);
    } else {
      // Create the intersection
      updateIntersection();
      setShowIntersection(true);
    }
  };

  // Function to toggle the NURBS surface
  const toggleNurbsSurface = () => {
    if (showNurbsSurface) {
      // Remove the surface
      if (nurbsSurfaceRef.current && sceneRef.current) {
        sceneRef.current.remove(nurbsSurfaceRef.current);
        nurbsSurfaceRef.current = null;
      }
      setShowNurbsSurface(false);
      
      // Also hide intersection if it's visible
      if (showIntersection) {
        if (intersectionRef.current && sceneRef.current) {
          sceneRef.current.remove(intersectionRef.current);
          intersectionRef.current = null;
        }
        setShowIntersection(false);
      }
    } else {
      // Create the surface
      updateNurbsSurface();
      setShowNurbsSurface(true);
      
      // Update intersection if it's visible
      if (showIntersection) {
        updateIntersection();
      }
    }
  };

  // Function to toggle the cylinder
  const toggleCylinder = () => {
    if (showCylinder) {
      // Remove the cylinder
      if (cylinderRef.current && sceneRef.current) {
        sceneRef.current.remove(cylinderRef.current);
        cylinderRef.current = null;
      }
      setShowCylinder(false);
      
      // Also hide intersection if it's visible
      if (showIntersection) {
        if (intersectionRef.current && sceneRef.current) {
          sceneRef.current.remove(intersectionRef.current);
          intersectionRef.current = null;
        }
        setShowIntersection(false);
      }
      
      // Also hide sphere-cylinder intersection if it's visible
      if (showSphereCylinderIntersection) {
        if (sphereCylinderIntersectionRef.current && sceneRef.current) {
          sceneRef.current.remove(sphereCylinderIntersectionRef.current);
          sphereCylinderIntersectionRef.current = null;
        }
        setShowSphereCylinderIntersection(false);
      }
    } else {
      // Create the cylinder
      updateCylinder();
      setShowCylinder(true);
      
      // Update intersection if it's visible
      if (showIntersection) {
        updateIntersection();
      }
      
      // Update sphere-cylinder intersection if it's visible
      if (showSphereCylinderIntersection) {
        updateSphereCylinderIntersection();
      }
    }
  };

  // Function to toggle the sphere
  const toggleSphere = () => {
    if (showSphere) {
      // Remove the sphere
      if (sphereRef.current && sceneRef.current) {
        sceneRef.current.remove(sphereRef.current);
        sphereRef.current = null;
      }
      setShowSphere(false);
      
      // Also hide sphere-cylinder intersection if it's visible
      if (showSphereCylinderIntersection) {
        if (sphereCylinderIntersectionRef.current && sceneRef.current) {
          sceneRef.current.remove(sphereCylinderIntersectionRef.current);
          sphereCylinderIntersectionRef.current = null;
        }
        setShowSphereCylinderIntersection(false);
      }
    } else {
      // Create the sphere
      updateSphere();
      setShowSphere(true);
      
      // Update sphere-cylinder intersection if it's visible
      if (showSphereCylinderIntersection) {
        updateSphereCylinderIntersection();
      }
    }
  };

  // Function to toggle the sphere-cylinder intersection
  const toggleSphereCylinderIntersection = () => {
    if (showSphereCylinderIntersection) {
      // Remove the intersection
      if (sphereCylinderIntersectionRef.current && sceneRef.current) {
        sceneRef.current.remove(sphereCylinderIntersectionRef.current);
        sphereCylinderIntersectionRef.current = null;
      }
      setShowSphereCylinderIntersection(false);
    } else {
      // Create the intersection
      updateSphereCylinderIntersection();
      setShowSphereCylinderIntersection(true);
    }
  };

  // Function to handle mouse click for adding points
  const handleMouseClick = (event: MouseEvent) => {
    if (!isAddingPoint || !sceneRef.current || !cameraRef.current) return;
    
    // Get mouse position
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Update raycaster
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    
    // Calculate intersection with the plane
    raycasterRef.current.ray.intersectPlane(planeRef.current, planeIntersectRef.current);
    
    if (planeIntersectRef.current) {
      // Add a new point at the intersection
      const newPoint = addControlPointAtPosition(planeIntersectRef.current.clone(), activeCurveIndex);
      
      // Select the new point
      if (newPoint) {
        setSelectedPoint(newPoint);
        updatePointColor(newPoint, selectedColorRef.current);
      }
    }
  };

  // Function to update point color
  const updatePointColor = (point: THREE.Mesh, color: number) => {
    if (point && point.material) {
      (point.material as THREE.MeshPhongMaterial).color.setHex(color);
    }
  };

  // Function to update cursor style
  const updateCursorStyle = (isHovering: boolean) => {
    if (containerRef.current) {
      containerRef.current.style.cursor = isHovering ? 'pointer' : (isAddingPoint ? 'crosshair' : 'default');
    }
  };

  // Initialize DragControls
  const initializeDragControls = () => {
    if (!sceneRef.current || !cameraRef.current || !rendererRef.current) return;
    
    // Get all points from all curves
    const allPoints: THREE.Mesh[] = [];
    splineCurvesRef.current.forEach(curve => {
      allPoints.push(...curve.points);
    });
    
    if (allPoints.length === 0) return;
    
    console.log("Initializing DragControls with points:", allPoints.length);
    
    // Initialize DragControls
    const dragControls = new DragControls(allPoints, cameraRef.current, rendererRef.current.domElement);
    dragControlsRef.current = dragControls;

    // DragControls event handlers
    dragControls.addEventListener('dragstart', (event) => {
      setIsDragging(true);
      if (controlsRef.current) {
        controlsRef.current.enabled = false;
      }
      
      // Store the initial position of the dragged object
      const draggedObject = event.object as THREE.Mesh;
      initialPositionRef.current.copy(draggedObject.position);
      
      // Change color to selected color
      updatePointColor(draggedObject, selectedColorRef.current);
      
      // Set as selected point
      setSelectedPoint(draggedObject);
      
      // Find which curve this point belongs to
      for (let i = 0; i < splineCurvesRef.current.length; i++) {
        if (splineCurvesRef.current[i].points.includes(draggedObject)) {
          setActiveCurveIndex(i);
          break;
        }
      }
    });

    dragControls.addEventListener('drag', (event) => {
      // Find the dragged object
      const draggedObject = event.object as THREE.Mesh;
      
      // Calculate the Y movement based on the difference from initial position
      const yDelta = draggedObject.position.y - initialPositionRef.current.y;
      
      // Set the position, keeping X and Z fixed at their initial values
      draggedObject.position.set(
        initialPositionRef.current.x,
        initialPositionRef.current.y + yDelta,
        initialPositionRef.current.z
      );
      
      // Add visual feedback for Y-axis movement
      const yValue = draggedObject.position.y;
      const normalizedY = Math.max(-1, Math.min(1, yValue / 5)); // Normalize Y value
      const yColor = new THREE.Color(0xff0000).offsetHSL(0, 0, normalizedY * 0.3);
      updatePointColor(draggedObject, yColor.getHex());
      
      // Update the spline for the active curve
      updateSpline(activeCurveIndex);
      
      // Update NURBS surface if it's visible
      if (showNurbsSurface) {
        updateNurbsSurface();
      }
    });

    dragControls.addEventListener('dragend', (event) => {
      setIsDragging(false);
      if (controlsRef.current) {
        controlsRef.current.enabled = true;
      }
      
      // Reset color to default
      const draggedObject = event.object as THREE.Mesh;
      updatePointColor(draggedObject, defaultColorRef.current);
    });
  };

  useEffect(() => {
    if (!containerRef.current) return;

    // Initialize scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf0f0f0);
    sceneRef.current = scene;

    // Initialize camera
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);
    camera.position.set(0, 5, 10);
    cameraRef.current = camera;

    // Initialize renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Add grid helper
    const gridHelper = new THREE.GridHelper(10, 10);
    scene.add(gridHelper);

    // Add axis helper
    const axisHelper = new THREE.AxesHelper(5);
    scene.add(axisHelper);

    // Initialize orbit controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controlsRef.current = controls;

    // Clear any existing curves
    splineCurvesRef.current = [];

    // Create 8 spline curves at different Z positions
    // Make the distance between first and second curves half compared to other curve distances
    const zPositions = [-7, -6, -3, -1, 1, 3, 5, 7];
    zPositions.forEach(zPosition => {
      const splineCurve = createSplineCurve(zPosition, scene);
      splineCurvesRef.current.push(splineCurve);
    });

    console.log("Created initial curves:", splineCurvesRef.current.length);

    // Add lights to make the scene look better
    const ambientLight = new THREE.AmbientLight(0x404040, 0.5);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
    directionalLight.position.set(1, 1, 1);
    directionalLight.castShadow = true;
    scene.add(directionalLight);

    // Add a second directional light for better illumination
    const directionalLight2 = new THREE.DirectionalLight(0xffffff, 0.5);
    directionalLight2.position.set(-1, -1, -1);
    scene.add(directionalLight2);

    // Add a point light for highlights
    const pointLight = new THREE.PointLight(0xffffff, 0.5, 10);
    pointLight.position.set(0, 5, 0);
    scene.add(pointLight);

    // Initialize DragControls
    initializeDragControls();

    // Mouse interaction setup
    const getMousePosition = (event: MouseEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return new THREE.Vector2();
      
      return new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
    };

    // Handle hovering
    const onMouseMove = (event: MouseEvent) => {
      if (isDragging) return;
      
      const mousePos = getMousePosition(event);
      
      // Update the raycaster
      raycasterRef.current.setFromCamera(mousePos, camera);
      
      // Get all points from all curves
      const allPoints: THREE.Mesh[] = [];
      splineCurvesRef.current.forEach(curve => {
        allPoints.push(...curve.points);
      });
      
      // Calculate objects intersecting the picking ray
      const intersects = raycasterRef.current.intersectObjects(allPoints, true);
      
      // Reset previously hovered point
      if (hoveredPointRef.current && hoveredPointRef.current !== selectedPoint) {
        updatePointColor(hoveredPointRef.current, defaultColorRef.current);
      }
      
      // Set new hovered point
      if (intersects.length > 0) {
        const hoveredPoint = intersects[0].object as THREE.Mesh;
        if (hoveredPoint !== selectedPoint) {
          updatePointColor(hoveredPoint, hoverColorRef.current);
          hoveredPointRef.current = hoveredPoint;
          updateCursorStyle(true);
        }
      } else {
        hoveredPointRef.current = null;
        updateCursorStyle(false);
      }
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('click', handleMouseClick);

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      const newWidth = containerRef.current.clientWidth;
      const newHeight = containerRef.current.clientHeight;
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(newWidth, newHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('click', handleMouseClick);
      if (dragControlsRef.current) {
        dragControlsRef.current.dispose();
      }
      containerRef.current?.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, [width, height, isDragging, handleMouseClick, initializeDragControls, selectedPoint, updateCursorStyle, createSplineCurve]);

  // Update DragControls when points are added or removed
  useEffect(() => {
    console.log("Point count changed:", pointCount, "Active curve:", activeCurveIndex);
    
    if (isInitializedRef.current) {
      // Dispose of old DragControls
      if (dragControlsRef.current) {
        dragControlsRef.current.dispose();
      }
      
      // Initialize new DragControls with updated points
      initializeDragControls();
    } else {
      isInitializedRef.current = true;
    }
  }, [pointCount, activeCurveIndex, initializeDragControls]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div style={{ position: 'absolute', top: '10px', left: '10px', background: 'rgba(255, 255, 255, 0.7)', padding: '10px', borderRadius: '5px' }}>
        <div>Control Points: {pointCount}</div>
        <div>Active Curve: {activeCurveIndex + 1} of {splineCurvesRef.current.length}</div>
        <div style={{ marginBottom: '10px', display: 'flex', flexWrap: 'wrap', maxWidth: '300px' }}>
          {splineCurvesRef.current.map((curve, index) => (
            <button 
              key={index}
              onClick={() => setActiveCurveIndex(index)}
              style={{ 
                marginRight: '5px',
                marginBottom: '5px',
                backgroundColor: activeCurveIndex === index ? '#4CAF50' : '#f0f0f0',
                color: activeCurveIndex === index ? 'white' : 'black'
              }}
            >
              Curve {index + 1}
            </button>
          ))}
        </div>
        <button 
          onClick={() => setIsAddingPoint(!isAddingPoint)} 
          style={{ 
            marginRight: '10px',
            backgroundColor: isAddingPoint ? '#4CAF50' : '#f0f0f0',
            color: isAddingPoint ? 'white' : 'black'
          }}
        >
          {isAddingPoint ? 'Cancel Adding' : 'Add Point'}
        </button>
        <button onClick={addControlPoint} style={{ marginRight: '10px' }}>Add Point at End</button>
        <button 
          onClick={removeSelectedPoint} 
          disabled={!selectedPoint || pointCount <= 2}
          style={{ marginRight: '10px' }}
        >
          Remove Selected
        </button>
        <button 
          onClick={toggleNurbsSurface}
          style={{ 
            marginRight: '10px',
            backgroundColor: showNurbsSurface ? '#4CAF50' : '#f0f0f0',
            color: showNurbsSurface ? 'white' : 'black'
          }}
        >
          {showNurbsSurface ? 'Hide NURBS Surface' : 'Show NURBS Surface'}
        </button>
        <button 
          onClick={toggleCylinder}
          style={{ 
            marginRight: '10px',
            backgroundColor: showCylinder ? '#4CAF50' : '#f0f0f0',
            color: showCylinder ? 'white' : 'black'
          }}
        >
          {showCylinder ? 'Hide Cylinder' : 'Show Cylinder'}
        </button>
        <button 
          onClick={toggleSphere}
          style={{ 
            marginRight: '10px',
            backgroundColor: showSphere ? '#4CAF50' : '#f0f0f0',
            color: showSphere ? 'white' : 'black'
          }}
        >
          {showSphere ? 'Hide Sphere' : 'Show Sphere'}
        </button>
        <button 
          onClick={toggleIntersection}
          style={{ 
            marginRight: '10px',
            backgroundColor: showIntersection ? '#4CAF50' : '#f0f0f0',
            color: showIntersection ? 'white' : 'black'
          }}
        >
          {showIntersection ? 'Hide NURBS-Cylinder Intersection' : 'Show NURBS-Cylinder Intersection'}
        </button>
        <button 
          onClick={toggleSphereCylinderIntersection}
          style={{ 
            marginRight: '10px',
            backgroundColor: showSphereCylinderIntersection ? '#4CAF50' : '#f0f0f0',
            color: showSphereCylinderIntersection ? 'white' : 'black'
          }}
        >
          {showSphereCylinderIntersection ? 'Hide Sphere-Cylinder Intersection' : 'Show Sphere-Cylinder Intersection'}
        </button>
        <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
          Note: The intersections are simplified visualizations of where the geometries meet.
        </div>
      </div>
    </div>
  );
};

export default SplineEditor; 